"use client";

import { BRAND_ASSETS } from "@/lib/brand-assets";
import { useEffect, useRef, useState } from "react";

interface AsciiLogoOverlayProps {
  className?: string;
  canvasClassName?: string;
  imageSrc?: string;
  isVisible: boolean;
  scrollProgress?: number;
  variant?: "default" | "ecole";
}

const WORD = "CHICKYTUTOR";
const MOUSE_RADIUS = 180;
const DESKTOP_OVERLAY_QUERY = "(min-width: 768px)";

function getCanvasContext(canvas: HTMLCanvasElement) {
  return canvas.getContext("2d", {
    alpha: true,
  });
}

export function AsciiLogoOverlay({
  className = "pointer-events-none fixed inset-0 z-[2] overflow-hidden opacity-[0.18] md:opacity-25",
  canvasClassName = "h-full w-full blur-[0.2px]",
  imageSrc = BRAND_ASSETS.mark.web,
  isVisible,
  scrollProgress = 0,
  variant = "default",
}: AsciiLogoOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const scrollRef = useRef(scrollProgress);
  const [matchesVisibleViewport, setMatchesVisibleViewport] = useState(false);
  const [hasSettledInitialPaint, setHasSettledInitialPaint] = useState(false);
  const shouldRender = isVisible && matchesVisibleViewport && hasSettledInitialPaint;

  useEffect(() => {
    scrollRef.current = scrollProgress;
  }, [scrollProgress]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_OVERLAY_QUERY);
    const updateViewportMatch = () => {
      setMatchesVisibleViewport(mediaQuery.matches);
    };

    updateViewportMatch();
    mediaQuery.addEventListener("change", updateViewportMatch);

    return () => {
      mediaQuery.removeEventListener("change", updateViewportMatch);
    };
  }, []);

  useEffect(() => {
    if (!isVisible || !matchesVisibleViewport) {
      setHasSettledInitialPaint(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setHasSettledInitialPaint(true);
    }, 900);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isVisible, matchesVisibleViewport]);

  useEffect(() => {
    if (!shouldRender) return;

    const handleMouseMove = (event: MouseEvent) => {
      mouseRef.current = { x: event.clientX, y: event.clientY, active: true };
    };
    const handleMouseLeave = () => {
      mouseRef.current = { ...mouseRef.current, active: false };
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("mouseout", handleMouseLeave, { passive: true });

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseout", handleMouseLeave);
    };
  }, [shouldRender]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !shouldRender) return;

    const context = getCanvasContext(canvas);
    if (!context) return;

    const image = new Image();
    image.decoding = "async";
    image.src = imageSrc;

    let frameId = 0;
    let disposed = false;
    let hasStarted = false;
    let lastDraw = -Infinity;
    const FRAME_MS = 32; // ~30fps cap for the ambient texture

    const sourceCanvas = document.createElement("canvas");
    const sourceContext = sourceCanvas.getContext("2d", { willReadFrequently: true });
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const textColor =
      variant === "ecole" ? "rgba(255, 214, 88, 0.78)" : "rgba(255, 174, 112, 0.78)";

    // Cached source sampling. The chicky only shifts with scroll, so we run the
    // expensive getImageData only when the grid size or scroll bucket changes.
    let srcCols = 0;
    let srcRows = 0;
    let srcScrollBucket = -1;
    let pixels: Uint8ClampedArray | null = null;
    let anchorX = 0;
    let anchorY = 0;

    const ensureSource = (width: number, height: number, scroll: number) => {
      const cell = width < 640 ? 12 : 14;
      const cols = Math.ceil(width / cell);
      const rows = Math.ceil(height / cell);
      const scrollBucket = Math.round(scroll * 40);

      if (pixels && cols === srcCols && rows === srcRows && scrollBucket === srcScrollBucket) {
        return { cell, cols, rows };
      }
      if (!sourceContext) return { cell, cols, rows };

      srcCols = cols;
      srcRows = rows;
      srcScrollBucket = scrollBucket;
      sourceCanvas.width = cols;
      sourceCanvas.height = rows;
      sourceContext.clearRect(0, 0, cols, rows);

      const maxLogoWidth = cols * 0.3;
      const maxLogoHeight = rows * 0.42;
      const logoScale = Math.min(
        maxLogoWidth / image.naturalWidth,
        maxLogoHeight / image.naturalHeight
      );
      const logoWidth = image.naturalWidth * logoScale;
      const logoHeight = image.naturalHeight * logoScale;
      // Scroll-reactive parallax drift (in grid cells).
      const parallax = scroll * rows * 0.16;
      // Anchor the chicky to the far lower-left so it stays clear of the centered card.
      const logoX = -cols * 0.01;
      const logoY = rows - logoHeight - rows * 0.05 - parallax;
      sourceContext.drawImage(image, logoX, logoY, logoWidth, logoHeight);

      anchorX = logoX + logoWidth / 2;
      anchorY = logoY + logoHeight / 2;
      pixels = sourceContext.getImageData(0, 0, cols, rows).data;

      return { cell, cols, rows };
    };

    const scheduleRender = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(render);
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Invalidate the cached source so it rebuilds at the new size.
      pixels = null;
      lastDraw = -Infinity;
      // Re-kick the loop so it recovers after the canvas regains size
      // (e.g. when crossing the mobile -> desktop breakpoint).
      if (reducedMotion) {
        render(0);
      } else {
        scheduleRender();
      }
    };

    const render = (time: number) => {
      if (disposed || !sourceContext || !image.complete || !image.naturalWidth) return;
      // Cap the work to ~30fps; the ambient texture doesn't need 60.
      if (!reducedMotion && time - lastDraw < FRAME_MS) {
        frameId = requestAnimationFrame(render);
        return;
      }
      lastDraw = time;

      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (!width || !height) return;

      const scroll = Math.min(1, Math.max(0, scrollRef.current));
      const { cell, cols, rows } = ensureSource(width, height, scroll);
      if (!pixels) {
        if (!reducedMotion) frameId = requestAnimationFrame(render);
        return;
      }

      context.clearRect(0, 0, width, height);
      context.font = `${Math.max(9, cell * 0.92)}px "Aeonik Mono Regular", "Courier New", monospace`;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillStyle = textColor;

      const flowPhase = reducedMotion ? 0 : time * 0.0006;
      const ripplePhase = reducedMotion ? 0 : time * 0.006;
      const mouse = mouseRef.current;
      const mouseActive = !reducedMotion && mouse.active;
      // As the user scrolls down, cells progressively dissolve away.
      const dissolveThreshold = scroll * 0.85;

      for (let y = 0; y < rows; y += 1) {
        for (let x = 0; x < cols; x += 1) {
          const index = (y * cols + x) * 4;
          const alpha = pixels[index + 3] / 255;
          const brightness =
            alpha > 0 ? (pixels[index] + pixels[index + 1] + pixels[index + 2]) / 765 : 0;

          // Ambient ASCII field, densest around the lower-left chicky anchor and
          // fading out with distance so the texture stays subtle elsewhere.
          const nx = (x - anchorX) / cols;
          const ny = (y - anchorY) / rows;
          const vignette = Math.max(0, 1 - Math.sqrt(nx * nx + ny * ny) * 1.75);
          const flow =
            Math.sin(x * 0.16 + flowPhase * 2.1) * 0.5 + Math.cos(y * 0.13 - flowPhase * 1.7) * 0.5;
          const ambient = (0.5 + flow * 0.22) * vignette;
          // The logo silhouette is embedded as a brighter, denser region.
          const logo = alpha * (0.5 + brightness * 0.5);
          let intensity = ambient * 0.55 + logo;

          // Stable per-cell noise for the scroll dissolve.
          const noise = (((Math.sin(x * 12.9898 + y * 78.233) * 43758.5453) % 1) + 1) % 1;
          if (noise < dissolveThreshold) continue;

          const baseX = x * cell + cell / 2;
          const baseY = y * cell + cell / 2;

          let mouseBoost = 0;
          let offsetX = 0;
          let offsetY = 0;
          if (mouseActive) {
            const dx = baseX - mouse.x;
            const dy = baseY - mouse.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < MOUSE_RADIUS) {
              const falloff = 1 - dist / MOUSE_RADIUS;
              mouseBoost = falloff * falloff;
              // Gentle ripple displacement away from the cursor.
              const ripple = Math.sin(dist * 0.08 - ripplePhase) * falloff * 2.4;
              const inv = dist > 0.001 ? 1 / dist : 0;
              offsetX = dx * inv * ripple;
              offsetY = dy * inv * ripple;
            }
          }

          intensity += mouseBoost * 0.6;
          const cellAlpha = Math.min(1, intensity * (0.92 + mouseBoost * 1.1));
          if (cellAlpha < 0.05) continue;

          // Spell the repeating brand word across the grid, shifted per row so it
          // reads as a woven stream of CHICKYTUTOR rather than vertical columns.
          const glyph = WORD[(x + y) % WORD.length];

          context.globalAlpha = cellAlpha;
          context.fillText(glyph, baseX + offsetX, baseY + offsetY);
        }
      }

      context.globalAlpha = 1;
      if (!reducedMotion) {
        frameId = requestAnimationFrame(render);
      }
    };

    const start = () => {
      if (hasStarted) return;
      hasStarted = true;
      resize();
    };

    // Stop the loop entirely while the tab is backgrounded; resume when visible.
    const handleVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(frameId);
      } else if (!reducedMotion && hasStarted) {
        lastDraw = -Infinity;
        scheduleRender();
      }
    };

    image.addEventListener("load", start, { once: true });
    if (image.complete) start();
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      disposed = true;
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", handleVisibility);
      image.removeEventListener("load", start);
      cancelAnimationFrame(frameId);
    };
  }, [imageSrc, shouldRender, variant]);

  if (!shouldRender) return null;

  return (
    <div className={className} aria-hidden="true">
      <canvas ref={canvasRef} className={canvasClassName} />
    </div>
  );
}
